import { Link } from "react-router-dom";
import MarketingNav from "@/components/marketing/MarketingNav";
import MarketingFooter from "@/components/marketing/MarketingFooter";
import {
  MessageSquare, CheckCircle2, Clock, Zap, AlertTriangle,
  Sparkles, Brain, Send, BarChart3, Shield
} from "lucide-react";

export default function HomePage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <MarketingNav />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-5xl sm:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Turn WhatsApp Into Work
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-lg">
                Automatically capture, organize, and act on your WhatsApp messages — never miss a follow-up again.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full transition"
                >
                  Get Started Free
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center px-8 py-3 border-2 border-slate-300 hover:border-blue-600 text-slate-900 hover:text-blue-600 font-semibold rounded-full transition"
                >
                  Learn More
                </a>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Already have an account?{" "}
                <Link to="/login" className="text-blue-600 hover:underline font-medium">
                  Log in →
                </Link>
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="bg-gradient-to-br from-blue-200 to-green-100 rounded-2xl h-96 flex items-center justify-center">
                <Sparkles size={64} className="text-blue-600 opacity-20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Losing business on WhatsApp?
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              These problems are costing your business every day
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: MessageSquare, title: "Messages Get Lost", desc: "Important messages disappear in endless chats" },
              { icon: Clock, title: "Follow-ups Forgotten", desc: "You forget to follow up on client commitments" },
              { icon: Zap, title: "Opportunities Missed", desc: "Sales leads and chances slip through the cracks" },
              { icon: AlertTriangle, title: "No Tracking", desc: "No way to track promises you've made" },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                <item.icon className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              BrainyDo fixes this automatically
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              AI-powered task creation and smart follow-up tracking
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { icon: Brain, title: "Smart Classification", desc: "AI automatically categorizes each message" },
              { icon: CheckCircle2, title: "Auto Task Creation", desc: "Convert messages into actionable tasks instantly" },
              { icon: Send, title: "AI Reply Drafts", desc: "Get smart reply suggestions powered by GPT" },
              { icon: BarChart3, title: "Unified Dashboard", desc: "Track all conversations and tasks in one place" },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                <item.icon className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              How it works in 5 steps
            </h2>
          </div>
          <div className="grid md:grid-cols-5 gap-6">
            {[
              "Connect WhatsApp",
              "Messages arrive in real-time",
              "AI classifies & extracts",
              "Tasks are auto-created",
              "Review & take action",
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
                  {i + 1}
                </div>
                <p className="font-heading font-semibold text-slate-900">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Powerful features built for you
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: "Smart Task Creation", desc: "Automatically converts WhatsApp messages into actionable tasks" },
              { title: "AI Reply Suggestions", desc: "Draft professional replies instantly using AI" },
              { title: "Auto Follow-ups", desc: "Never miss a follow-up with smart reminders" },
              { title: "WhatsApp Integration", desc: "Seamless connection with your WhatsApp Business" },
              { title: "Dashboard & Tracking", desc: "Centralized view of all conversations and tasks" },
              { title: "Smart Notifications", desc: "Get alerts for urgent items and pending actions" },
            ].map((feature, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition">
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Start managing WhatsApp like a pro
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Join businesses that never miss a follow-up again
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full transition"
            >
              Start Free Trial
            </Link>
            <a
              href="mailto:contact@brainyd.com"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-slate-300 hover:border-blue-600 text-slate-900 hover:text-blue-600 font-semibold rounded-full transition"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
